import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from '../App';
import axios from 'axios';
import Navigation from '../components/Navigation';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Mail, Send, MessageCircle, ArrowLeft, CheckCircle2, Inbox, CornerDownRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const ContactAdmin = () => {
  const { user, API } = useContext(AuthContext);
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  // Per-thread reply state
  const [replyDrafts, setReplyDrafts] = useState({}); // { [threadId]: text }
  const [replyingThreadId, setReplyingThreadId] = useState(null);

  useEffect(() => {
    fetchMyMessages();
    fetchInbox();
  }, []);

  const fetchMyMessages = async () => {
    try {
      const response = await axios.get(`${API}/messages/my`);
      setMessages(response.data);
    } catch (error) {
      console.error('Failed to load messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchInbox = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/messages/inbox`);
      setThreads(response.data || []);
      // Mark unread messages (where I am recipient) as read on open
      const idsToMark = [];
      for (const t of response.data || []) {
        for (const m of t.messages || []) {
          if (m.recipient_id === user?.id && !m.read_by_recipient) {
            idsToMark.push(m.id);
          }
        }
      }
      await Promise.all(
        idsToMark.map((id) =>
          axios.post(`${API}/messages/inbox/${id}/read`).catch(() => {})
        )
      );
    } catch (error) {
      console.error('Failed to load inbox:', error);
    }
  }, [API, user?.id]);

  const handleSendReply = async (thread) => {
    const text = (replyDrafts[thread.thread_id] || '').trim();
    if (!text) {
      toast.error('Please write a reply');
      return;
    }
    // Reply is sent against the newest message in the thread (that we can act on).
    const anchor = thread.messages[thread.messages.length - 1];
    setReplyingThreadId(thread.thread_id);
    try {
      await axios.post(`${API}/messages/inbox/${anchor.id}/reply`, { message: text });
      setReplyDrafts((prev) => ({ ...prev, [thread.thread_id]: '' }));
      toast.success('Reply sent');
      fetchInbox();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send reply');
    } finally {
      setReplyingThreadId(null);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!message.trim()) {
      toast.error('Please enter a message');
      return;
    }

    setSending(true);
    try {
      await axios.post(`${API}/messages/admin`, {
        subject: subject.trim(),
        message: message.trim()
      });
      
      toast.success('Message sent to admin!');
      setSubject('');
      setMessage('');
      fetchMyMessages();
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-teal-50">
      <Navigation />
      
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="mb-4 text-slate-600 hover:text-slate-800"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-[#2B6B7F]/10 flex items-center justify-center">
              <Mail className="w-6 h-6 text-[#2B6B7F]" />
            </div>
            <div>
              <h1 className="text-3xl font-heading font-bold text-[#2B6B7F]">
                Contact Admin
              </h1>
              <p className="text-slate-600">Send a private message to the church admin</p>
            </div>
          </div>
        </div>

        {/* Inbox — threaded conversations with admin / prayer moderators */}
        {threads.length > 0 && (
          <Card className="mb-8" data-testid="direct-message-inbox">
            <CardHeader>
              <CardTitle className="text-xl text-[#2B6B7F] flex items-center gap-2">
                <Inbox className="w-5 h-5" />
                From the Team
              </CardTitle>
              <CardDescription>
                Direct conversations with an admin or prayer moderator
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {threads.map((t) => {
                  const otherName =
                    t.root_sender_id === user?.id ? t.root_recipient_name : t.root_sender_name;
                  const otherRole =
                    t.root_sender_id === user?.id ? null : t.root_sender_role;
                  return (
                    <div
                      key={t.thread_id}
                      className="rounded-lg border border-[#2B6B7F]/20 bg-white overflow-hidden"
                      data-testid={`inbox-thread-${t.thread_id}`}
                    >
                      <div className="p-3 bg-[#2B6B7F]/5 border-b border-[#2B6B7F]/10 flex items-center justify-between gap-2 flex-wrap">
                        <div className="flex items-center gap-2 min-w-0">
                          <MessageCircle className="w-4 h-4 text-[#2B6B7F] flex-shrink-0" />
                          <span className="text-sm font-semibold text-[#2B6B7F] truncate">
                            {otherName || 'Team'}
                          </span>
                          {otherRole && (
                            <Badge variant="outline" className="text-[10px] capitalize">
                              {otherRole === 'prayer_moderator' ? 'prayer moderator' : otherRole}
                            </Badge>
                          )}
                          {t.subject && (
                            <Badge variant="outline" className="text-xs">
                              {t.subject}
                            </Badge>
                          )}
                        </div>
                        {t.unread_count > 0 && (
                          <Badge className="bg-[#2B6B7F]">
                            {t.unread_count} new
                          </Badge>
                        )}
                      </div>

                      <div className="p-3 space-y-2">
                        {t.messages.map((m) => {
                          const mine = m.sender_id === user?.id;
                          return (
                            <div
                              key={m.id}
                              className={`flex ${mine ? 'justify-end' : 'justify-start'}`}
                              data-testid={`inbox-msg-${m.id}`}
                            >
                              <div
                                className={`max-w-[80%] rounded-2xl px-3 py-2 ${
                                  mine
                                    ? 'bg-[#2B6B7F] text-white rounded-br-sm'
                                    : 'bg-slate-100 text-slate-800 rounded-bl-sm'
                                }`}
                              >
                                <div className={`text-[10px] mb-0.5 ${mine ? 'text-white/70' : 'text-slate-500'}`}>
                                  {mine ? 'You' : m.sender_name} · {formatDate(m.created_at)}
                                </div>
                                <p className="text-sm whitespace-pre-wrap break-words">
                                  {m.message}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      <div className="p-3 border-t border-slate-100 bg-slate-50/60">
                        <div className="flex items-end gap-2">
                          <Textarea
                            placeholder="Write a reply…"
                            value={replyDrafts[t.thread_id] || ''}
                            onChange={(e) =>
                              setReplyDrafts((prev) => ({
                                ...prev,
                                [t.thread_id]: e.target.value,
                              }))
                            }
                            rows={2}
                            maxLength={4000}
                            className="text-sm min-h-[52px] resize-none"
                            data-testid={`inbox-reply-input-${t.thread_id}`}
                          />
                          <Button
                            size="sm"
                            onClick={() => handleSendReply(t)}
                            disabled={
                              replyingThreadId === t.thread_id ||
                              !(replyDrafts[t.thread_id] || '').trim()
                            }
                            className="bg-[#2B6B7F] flex-shrink-0"
                            data-testid={`inbox-reply-send-${t.thread_id}`}
                          >
                            <CornerDownRight className="w-4 h-4 mr-1" />
                            {replyingThreadId === t.thread_id ? '…' : 'Reply'}
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* New Message Form */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-xl text-[#2B6B7F] flex items-center gap-2">
              <Send className="w-5 h-5" />
              Send a Message
            </CardTitle>
            <CardDescription>
              Your message will only be seen by the admin
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSendMessage} className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">
                  Subject (optional)
                </label>
                <Input
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="What's this about?"
                  className="w-full"
                />
              </div>
              
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">
                  Message *
                </label>
                <Textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Type your message here..."
                  rows={5}
                  className="w-full resize-none"
                  required
                />
              </div>
              
              <Button
                type="submit"
                disabled={sending || !message.trim()}
                className="w-full bg-[#2B6B7F] hover:bg-[#1E4D5F]"
              >
                {sending ? (
                  'Sending...'
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Message
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* My Messages History */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl text-[#2B6B7F] flex items-center gap-2">
              <MessageCircle className="w-5 h-5" />
              My Messages
            </CardTitle>
            <CardDescription>
              Your conversation history with the admin
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-slate-500">Loading...</div>
            ) : messages.length === 0 ? (
              <div className="text-center py-8">
                <Mail className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No messages yet</p>
                <p className="text-sm text-slate-400">Send your first message above</p>
              </div>
            ) : (
              <div className="space-y-4">
                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`rounded-lg border ${msg.admin_reply ? 'border-green-200 bg-green-50/50' : 'border-slate-200 bg-white'}`}
                  >
                    {/* User's Message */}
                    <div className="p-4 border-b border-slate-100">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-slate-700">You</span>
                          {msg.subject && (
                            <Badge variant="outline" className="text-xs">
                              {msg.subject}
                            </Badge>
                          )}
                        </div>
                        <span className="text-xs text-slate-500">{formatDate(msg.created_at)}</span>
                      </div>
                      <p className="text-slate-700 whitespace-pre-wrap">{msg.message}</p>
                    </div>
                    
                    {/* Admin Reply */}
                    {msg.admin_reply ? (
                      <div className="p-4 bg-[#2B6B7F]/5">
                        <div className="flex items-center gap-2 mb-2">
                          <CheckCircle2 className="w-4 h-4 text-green-600" />
                          <span className="text-sm font-medium text-[#2B6B7F]">Admin Reply</span>
                          <span className="text-xs text-slate-500">{formatDate(msg.replied_at)}</span>
                        </div>
                        <p className="text-slate-700 whitespace-pre-wrap">{msg.admin_reply}</p>
                      </div>
                    ) : (
                      <div className="p-3 bg-slate-50 text-center">
                        <span className="text-xs text-slate-500">Awaiting reply...</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ContactAdmin;
